import { useState, useEffect } from 'react';
import { Collapse, Typography, Space, Button, Tag, Card } from 'antd';
import {
	DeleteOutlined,
	BugOutlined,
	ClockCircleOutlined,
	RightOutlined,
	LeftOutlined,
	DownloadOutlined,
} from '@ant-design/icons';
import { useDebug } from '@/libs/debug/DebugContext';
import { formatDistanceToNow } from 'date-fns';

const { Panel } = Collapse;
const { Text, Paragraph } = Typography;

const DebugPanel: React.FC = () => {
	const { apiLogs, clearApiLogs, isDebugMode, setDebugMode } = useDebug();
	const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
	const [isCollapsed, setIsCollapsed] = useState(() => {
		// Load collapsed state from localStorage
		const saved = localStorage.getItem('debug-panel-collapsed');
		return saved ? JSON.parse(saved) : false;
	});

	// Check URL for debug mode on mount and URL changes
	useEffect(() => {
		const checkDebugMode = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const isDebug = urlParams.get('debug') === 'true';
			setDebugMode(isDebug);
		};

		// Check on mount
		checkDebugMode();

		// Listen for URL changes
		const handlePopState = () => checkDebugMode();
		window.addEventListener('popstate', handlePopState);

		// Override pushState and replaceState to detect programmatic navigation
		const originalPushState = history.pushState;
		const originalReplaceState = history.replaceState;

		history.pushState = function (...args) {
			originalPushState.apply(history, args);
			setTimeout(checkDebugMode, 100);
		};

		history.replaceState = function (...args) {
			originalReplaceState.apply(history, args);
			setTimeout(checkDebugMode, 100);
		};

		return () => {
			window.removeEventListener('popstate', handlePopState);
			history.pushState = originalPushState;
			history.replaceState = originalReplaceState;
		};
	}, [setDebugMode]);

	// Add debug mode class to body for CSS adjustments
	useEffect(() => {
		if (isDebugMode) {
			document.body.classList.add('debug-mode-active');
		} else {
			document.body.classList.remove('debug-mode-active');
		}

		return () => {
			document.body.classList.remove('debug-mode-active');
		};
	}, [isDebugMode]);

	// Save collapsed state to localStorage
	useEffect(() => {
		localStorage.setItem('debug-panel-collapsed', JSON.stringify(isCollapsed));
	}, [isCollapsed]);

	// Keyboard shortcut handler (Ctrl+Shift+D)
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.ctrlKey && event.shiftKey && event.key === 'D') {
				event.preventDefault();
				setIsCollapsed((prev: boolean) => !prev);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	// Toggle function
	const toggleCollapsed = () => {
		setIsCollapsed((prev: boolean) => !prev);
	};

	if (!isDebugMode) return null;

	const handlePanelChange = (keys: string | string[]) => {
		setExpandedKeys(Array.isArray(keys) ? keys : [keys]);
	};

	const formatEndpoint = (endpoint: string) => {
		// Remove base URL and show only the API path
		return endpoint.replace(/^https?:\/\/[^\\/]+/, '').replace(/\/$/, ''); // Remove trailing slash
	};

	const getStatusColor = (status?: number) => {
		if (!status) return 'default';
		if (status >= 200 && status < 300) return 'success';
		if (status >= 400 && status < 500) return 'warning';
		return 'error';
	};

	const formatDuration = (duration: number) => {
		if (duration < 1000) return `${duration}ms`;
		return `${(duration / 1000).toFixed(2)}s`;
	};

	const formatPayload = (payload: unknown) => {
		try {
			return JSON.stringify(payload, null, 2);
		} catch {
			return String(payload);
		}
	};

	// Download debug data as JSON
	const downloadDebugData = () => {
		try {
			// Get user information
			const userInfo = sessionStorage.getItem('fleet-user-info');
			let user = null;
			if (userInfo) {
				user = JSON.parse(userInfo);
			}

			// Prepare debug data
			const debugData = {
				metadata: {
					exportedAt: new Date().toISOString(),
					user: user
						? {
								id: user.id,
								email: user.email,
								role: user.role,
								name: user.name,
							}
						: null,
					totalCalls: apiLogs.length,
				},
				apiLogs: apiLogs
					.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()) // Chronological order
					.map((call) => ({
						id: call.id,
						timestamp: call.timestamp.toISOString(),
						endpoint: call.endpoint,
						method: call.method,
						status: call.status,
						duration: call.duration,
						payload: call.payload,
						response: call.response,
						error: call.error,
						isError: call.isError,
					})),
			};

			// Create and download JSON file
			const jsonString = JSON.stringify(debugData, null, 2);
			const blob = new Blob([jsonString], { type: 'application/json' });
			const url = URL.createObjectURL(blob);

			const link = document.createElement('a');
			link.href = url;
			link.download = `debug-api-calls-${new Date().toISOString().split('T')[0]}.json`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Failed to download debug data:', error);
		}
	};

	return (
		<>
			{/* CSS for layout adjustments */}
			<style>
				{/* {`
					.debug-mode-active .ant-layout-content {
						margin-left: ${isCollapsed ? '50px' : '30%'} !important;
					}
					.debug-mode-active .ant-layout-sider {
						left: ${isCollapsed ? '50px' : '30%'} !important;
					}
				`} */}
			</style>

			{/* Collapsed Panel */}
			{isCollapsed ? (
				<div
					style={{
						position: 'fixed',
						left: 0,
						top: '50%',
						transform: 'translateY(-50%)',
						width: '40px',
						height: 'auto',
						backgroundColor: '#fafafa',
						borderRight: '1px solid #d9d9d9',
						borderTop: '1px solid #d9d9d9',
						borderBottom: '1px solid #d9d9d9',
						borderTopLeftRadius: '4px',
						borderBottomLeftRadius: '4px',
						zIndex: 1000,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						padding: '8px 4px',
						boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
					}}
				>
					<Button
						type="text"
						icon={<RightOutlined />}
						onClick={toggleCollapsed}
						size="small"
						title="Expand Debug Panel (Ctrl+Shift+D)"
						style={{ color: '#1890ff', marginBottom: '8px' }}
					/>
					<BugOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
					<Tag
						color="blue"
						style={{
							fontSize: '10px',
							marginTop: '4px',
							transform: 'rotate(-90deg)',
							transformOrigin: 'center',
							whiteSpace: 'nowrap',
							padding: '2px 4px',
						}}
					>
						{apiLogs.length}
					</Tag>
				</div>
			) : (
				/* Expanded Panel */
				<div
					style={{
						position: 'fixed',
						left: 0,
						top: '100px', // Below header
						width: '30%',
						height: 'calc(100vh - 100px)',
						backgroundColor: '#fafafa',
						borderRight: '1px solid #d9d9d9',
						zIndex: 1000,
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					{/* Header */}
					<Card
						size="small"
						style={{
							borderRadius: 0,
							borderLeft: 'none',
							borderTop: 'none',
							borderRight: 'none',
						}}
					>
						<Space style={{ width: '100%', justifyContent: 'space-between' }}>
							<Space>
								<BugOutlined style={{ color: '#1890ff' }} />
								<Text strong>API Call Logs</Text>
								<Tag color="blue">{apiLogs.length} calls</Tag>
							</Space>
							<Space>
								<Button
									type="text"
									size="small"
									icon={<DownloadOutlined />}
									onClick={downloadDebugData}
									title="Download Debug Data as JSON"
									disabled={apiLogs.length === 0}
								/>
								<Button
									type="text"
									size="small"
									icon={<LeftOutlined />}
									onClick={toggleCollapsed}
									title="Collapse Debug Panel (Ctrl+Shift+D)"
								/>
								<Button
									size="small"
									icon={<DeleteOutlined />}
									onClick={clearApiLogs}
									danger
								>
									Clear
								</Button>
							</Space>
						</Space>
					</Card>

					{/* Content */}
					<div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
						{apiLogs.length === 0 ? (
							<div
								style={{ textAlign: 'center', padding: '20px', color: '#999' }}
							>
								<BugOutlined
									style={{ fontSize: '24px', marginBottom: '8px' }}
								/>
								<Text type="secondary">No API calls logged yet</Text>
							</div>
						) : (
							<Collapse
								activeKey={expandedKeys}
								onChange={handlePanelChange}
								ghost
								size="small"
							>
								{apiLogs.map((call) => (
									<Panel
										key={call.id}
										header={
											<div style={{ width: '100%' }}>
												<Space
													direction="vertical"
													size={0}
													style={{ width: '100%' }}
												>
													<div
														style={{
															display: 'flex',
															justifyContent: 'space-between',
															alignItems: 'center',
														}}
													>
														<Text
															code
															style={{ fontSize: '11px', maxWidth: '60%' }}
														>
															{formatEndpoint(call.endpoint)}
														</Text>
														<Space size={4}>
															<Tag color={getStatusColor(call.status)}>
																{call.status || 'N/A'}
															</Tag>
															<Tag icon={<ClockCircleOutlined />}>
																{formatDuration(call.duration)}
															</Tag>
														</Space>
													</div>
													<div
														style={{
															display: 'flex',
															justifyContent: 'space-between',
															alignItems: 'center',
														}}
													>
														<Tag color="blue">{call.method}</Tag>
														<Text
															type="secondary"
															style={{ fontSize: '10px' }}
														>
															{formatDistanceToNow(call.timestamp, {
																addSuffix: true,
															})}
														</Text>
													</div>
												</Space>
											</div>
										}
										style={{ marginBottom: '8px' }}
									>
										<Space
											direction="vertical"
											size={8}
											style={{ width: '100%' }}
										>
											{call.payload != null && (
												<div>
													<Text strong>Request Payload:</Text>
													<Paragraph
														code
														style={{
															backgroundColor: '#f5f5f5',
															padding: '8px',
															margin: '4px 0',
															fontSize: '11px',
															maxHeight: '200px',
															overflow: 'auto',
														}}
													>
														{formatPayload(call.payload)}
													</Paragraph>
												</div>
											)}

											{call.response != null && (
												<div>
													<Text
														strong
														style={{ color: '#52c41a' }}
													>
														Response:
													</Text>
													<Paragraph
														code
														style={{
															backgroundColor: '#f6ffed',
															padding: '8px',
															margin: '4px 0',
															fontSize: '11px',
															maxHeight: '200px',
															overflow: 'auto',
															border: '1px solid #b7eb8f',
														}}
													>
														{formatPayload(call.response)}
													</Paragraph>
												</div>
											)}

											{call.error != null && (
												<div>
													<Text
														strong
														type="danger"
													>
														Error:
													</Text>
													<Paragraph
														code
														style={{
															backgroundColor: '#fff2f0',
															padding: '8px',
															margin: '4px 0',
															fontSize: '11px',
															maxHeight: '200px',
															overflow: 'auto',
															border: '1px solid #ffccc7',
														}}
													>
														{formatPayload(call.error)}
													</Paragraph>
												</div>
											)}
										</Space>
									</Panel>
								))}
							</Collapse>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default DebugPanel;
